import { type ImpactType } from "@s2z-platform/constants";
import type { TStoreImpactConfigEntity } from "@s2z-platform/types";
import BigNumber from "bignumber.js";
import type { ImpactConfigModeType } from "./utils";

/**
 * Minimal percentage-range shape these helpers actually read. Kept loose so
 * entity, DTO, partial and form-value ranges are all assignable.
 */
export type ConfigPercentageRange = {
  percentage: number;
  amountFrom?: number | null;
  amountTo?: number | null;
};

/** Fields needed to determine if a config produces impact */
export type ConfigProducesImpactArgs = Pick<
  TStoreImpactConfigEntity,
  | "mode"
  | "isImpactPerBatch"
  | "impactPerOrder"
  | "impactPerBatchCount"
  | "isPercentagePerRange"
  | "percentagePerOrder"
> & {
  percentageRanges?: ConfigPercentageRange[];
};

/**
 * Determines if a single impact config will produce any impact based on its mode and values.
 * Accepts any object matching the shape (entity, DTO, partial, form values).
 */
export function configProducesImpact(
  config: ConfigProducesImpactArgs,
): boolean {
  if (config.mode === "fixed") {
    if (config.isImpactPerBatch) {
      return (config.impactPerBatchCount ?? 0) > 0;
    }
    return (config.impactPerOrder ?? 0) > 0;
  }
  if (config.mode === "percentage") {
    if (config.isPercentagePerRange) {
      return (config.percentageRanges ?? []).some((r) => r.percentage > 0);
    }
    return (config.percentagePerOrder ?? 0) > 0;
  }
  return false;
}

/**
 * Filters configs to only those that produce impact (value > 0).
 */
export function filterProducingConfigs<T extends ConfigProducesImpactArgs>(
  configs: T[] | undefined | null,
): T[] {
  return configs?.filter(configProducesImpact) ?? [];
}

/**
 * Find the impact config for a given impact type from a store's configs.
 * Returns undefined if not found or configs not loaded.
 */
export function findConfigByType<
  T extends { type: string } = TStoreImpactConfigEntity,
>(configs: T[] | undefined | null, type: ImpactType): T | undefined {
  return configs?.find((c) => c.type === type);
}

/**
 * Check if a store has any impact configs loaded.
 */
export function hasImpactConfigs(
  configs: { type: string }[] | undefined | null,
): boolean {
  return !!configs?.length;
}

/**
 * Convert an array of impact configs into a record keyed by impact type.
 * E.g. { trees: config, co2e: config, plastic: config }
 */
export function impactConfigsByType<
  T extends { type: string } = TStoreImpactConfigEntity,
>(configs: T[] | undefined | null): Partial<Record<ImpactType, T>> {
  if (!configs?.length) return {};
  return configs.reduce(
    (acc, config) => {
      acc[config.type as ImpactType] = config;
      return acc;
    },
    {} as Partial<Record<ImpactType, T>>,
  );
}

/**
 * NOTE: This type is largely the same as OffsetPercentageRangeFormData,
 * but numeric form inputs return strings, not numbers,
 * which that type reflects.
 *
 * Changes made here likely need to be made there too.
 */
export type OffsetPercentageRange = {
  amountTo?: number | null;
  amountFrom?: number | null;
  percentage?: number | null;
};

export type ComputeTotalArg = {
  avgCheckoutValue?: number;
  avgCheckoutVolume?: number;
  mode?: ImpactConfigModeType;
  isPercentagePerRange?: boolean | "0" | "1" | number;
  percentageRanges?: OffsetPercentageRange[];
  percentagePerOrder?: number;
  isImpactPerBatch?: boolean | "0" | "1" | number;
  impactPerOrder?: number;
  impactPerBatchAmount?: number;
  impactPerBatchCount?: number;
  minImpactPerBatch?: number;
  minOrderValue?: number;
  currencyToUse?: string;
};

/**
 * Expects and returns costs in WORKING units
 */
export function computeIntegrationCost(
  {
    avgCheckoutVolume = 0,
    avgCheckoutValue = 0,
    mode,
    percentagePerOrder = 0,
    isPercentagePerRange,
    percentageRanges = [],
    impactPerOrder = 0,
    isImpactPerBatch,
    impactPerBatchAmount = 0,
    impactPerBatchCount = 0,
    minImpactPerBatch,
    minOrderValue,
  }: ComputeTotalArg,
  treeCost: number,
): BigNumber {
  let total: BigNumber = new BigNumber(0);

  if (mode === "percentage") {
    let percentage: BigNumber = new BigNumber(0);
    if (
      isPercentagePerRange === true ||
      isPercentagePerRange === "1" ||
      isPercentagePerRange === 1
    ) {
      let totalPercentage = 0;
      percentageRanges.forEach((range) => {
        totalPercentage += +(range.percentage ?? 0);
      });
      const nbRanges = Math.max(1, percentageRanges.length);
      percentage = new BigNumber(totalPercentage).dividedBy(nbRanges);
    } else {
      percentage = new BigNumber(+percentagePerOrder);
    }
    total = new BigNumber(avgCheckoutValue)
      .multipliedBy(percentage)
      .multipliedBy(avgCheckoutVolume);
  } else if (mode === "fixed") {
    // If average order value is below minOrderValue, no impacts are generated
    if (minOrderValue && avgCheckoutValue < minOrderValue) {
      return total; // 0
    }

    if (
      isImpactPerBatch === true ||
      isImpactPerBatch === "1" ||
      isImpactPerBatch === 1
    ) {
      let units: BigNumber = new BigNumber(0);
      if (impactPerBatchAmount >= 0 && impactPerBatchCount >= 0) {
        units = new BigNumber(avgCheckoutValue)
          .dividedBy(+impactPerBatchAmount)
          .multipliedBy(+impactPerBatchCount);
      }
      units = new BigNumber(Math.floor(units.toNumber()));
      total = units.multipliedBy(treeCost).multipliedBy(avgCheckoutVolume);
      // If total is 0 and minImpactPerBatch >= 1, then result = treeCost
      if (total.toNumber() < 1 && (minImpactPerBatch ?? 0) >= 1) {
        total = new BigNumber(treeCost);
      }
    } else {
      total = new BigNumber(impactPerOrder)
        .multipliedBy(treeCost)
        .multipliedBy(avgCheckoutVolume);
    }
  }

  return total;
}
